import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

// Shared animation
export const EASE = [0.16, 1, 0.3, 1];

export const Container = ({ className, children }) => (
  <div className={cn("mx-auto w-full max-w-7xl px-6 md:px-10", className)}>
    {children}
  </div>
);

export const Overline = ({ children, className }) => (
  <span
    className={cn(
      "inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-axor-cyan",
      className,
    )}
  >
    <span className="h-px w-6 bg-gradient-to-r from-axor-cyan to-transparent" />
    {children}
  </span>
);

export const Reveal = ({ children, className, delay = 0, y = 28, as = "div" }) => {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      initial={{ opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.9, ease: EASE, delay }}
    >
      {children}
    </MotionTag>
  );
};

export const SectionHeading = ({
  overline,
  title,
  highlight,
  description,
  align = "left",
  className,
}) => {
  const renderTitle = () => {
    if (!highlight || typeof title !== "string") return title;
    const parts = title.split(highlight);
    return (
      <>
        {parts[0]}
        <span className="text-gradient-cyan">{highlight}</span>
        {parts[1]}
      </>
    );
  };
  return (
    <div
      className={cn(
        "flex flex-col gap-5",
        align === "center" && "items-center text-center",
        className,
      )}
    >
      {overline && <Overline>{overline}</Overline>}
      <h2 className="max-w-3xl text-4xl font-semibold leading-[1.08] tracking-tight text-axor-white sm:text-5xl">
        {renderTitle()}
      </h2>
      {description && (
        <p
          className={cn(
            "max-w-2xl text-lg leading-relaxed text-axor-text",
            align === "center" && "mx-auto",
          )}
        >
          {description}
        </p>
      )}
    </div>
  );
};

export const GlassCard = ({ className, children, hover = true, ...props }) => (
  <div
    className={cn(
      "glass-card rounded-[24px] md:rounded-[28px]",
      hover &&
        "transition-[background-color,border-color,box-shadow,transform] duration-500 hover:-translate-y-1 hover:border-white/15 hover:bg-white/[0.06] hover:shadow-[0_16px_48px_rgba(22,184,212,0.15)]",
      className,
    )}
    {...props}
  >
    {children}
  </div>
);

// Polymorphic premium button: renders <Link>, <a> or <button>
export const AxorButton = React.forwardRef(
  (
    { variant = "primary", to, href, className, children, icon: Icon, ...props },
    ref,
  ) => {
    const base =
      "group inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-bold tracking-wide transition-[background-color,box-shadow,transform,border-color,color] duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-axor-cyan focus-visible:ring-offset-2 focus-visible:ring-offset-axor-hero disabled:opacity-60";
    const variants = {
      primary: "btn-primary hover:scale-[1.03]",
      secondary:
        "glass-panel border border-white/15 text-axor-white hover:border-axor-aqua/50 hover:bg-white/10",
      ghost:
        "text-axor-white/80 hover:text-axor-white border border-transparent hover:border-white/10",
    };
    const content = (
      <>
        {children}
        {Icon && (
          <Icon className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5" />
        )}
      </>
    );
    const classes = cn(base, variants[variant], className);
    if (to)
      return (
        <Link ref={ref} to={to} className={classes} {...props}>
          {content}
        </Link>
      );
    if (href)
      return (
        <a ref={ref} href={href} className={classes} {...props}>
          {content}
        </a>
      );
    return (
      <button ref={ref} className={classes} {...props}>
        {content}
      </button>
    );
  },
);
AxorButton.displayName = "AxorButton";
